import { useState, useEffect, useCallback } from "react";
import THEME from "../../../theme";
import { fs } from "../utils/styles";
import { finanzenApi } from "../services/finanzenApi";

export default function WiederkehrendeZahlungenSeite({ benutzerId }) {
  const [kandidaten, setKandidaten] = useState([]);
  const [bestaetigte, setBestaetigte] = useState([]);
  const [kategorien, setKategorien] = useState([]);
  const [ignoriert, setIgnoriert] = useState(new Set());
  const [fehler, setFehler] = useState("");
  const [laedt, setLaedt] = useState(false);

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler("");
    try {
      const [k, b] = await Promise.all([
        finanzenApi.wiederkehrendKandidaten(benutzerId),
        finanzenApi.wiederkehrendeZahlungenListe(benutzerId),
      ]);
      setKandidaten(k);
      setBestaetigte(b);
    } catch (e) {
      setFehler(e.message);
    }
    setLaedt(false);
  }, [benutzerId]);

  useEffect(() => { laden(); }, [laden]);
  useEffect(() => { finanzenApi.kategorienListe(benutzerId).then(setKategorien).catch(() => {}); }, [benutzerId]);

  const kategorieName = (id) => kategorien.find(k => k.id === id)?.name || "–";

  const bestaetigen = async (kandidat) => {
    try {
      await finanzenApi.wiederkehrendBestaetigen(benutzerId, {
        name: kandidat.zahlungspartner_name || "Wiederkehrende Zahlung",
        zahlungspartner_name: kandidat.zahlungspartner_name,
        zahlungspartner_iban: kandidat.zahlungspartner_iban,
        kategorie_id: kandidat.kategorie_id,
        erwarteter_betrag: kandidat.durchschnitts_betrag,
        rhythmus: "monatlich",
        bankumsatz_ids: kandidat.bankumsatz_ids,
      });
      laden();
    } catch (e) {
      setFehler(e.message);
    }
  };

  const kategorieZuweisen = async (kandidat) => {
    if (kategorien.length === 0) return;
    const namen = kategorien.map((k, i) => `${i + 1}: ${k.name}`).join("\n");
    const eingabe = window.prompt(`Kategorie für „${kandidat.zahlungspartner_name}":\n\n${namen}`);
    const index = Number(eingabe) - 1;
    if (!eingabe || index < 0 || index >= kategorien.length) return;
    try {
      await finanzenApi.bankumsaetzeBulkBearbeiten(benutzerId, { ids: kandidat.bankumsatz_ids, kategorie_id: kategorien[index].id });
      laden();
    } catch (e) {
      setFehler(e.message);
    }
  };

  const kandidatIgnorieren = async (kandidat) => {
    setIgnoriert(prev => new Set(prev).add(kandidat.gruppen_schluessel));
    try {
      await finanzenApi.bankumsaetzeBulkBearbeiten(benutzerId, { ids: kandidat.bankumsatz_ids, verarbeitungs_status: "ignoriert" });
      laden();
    } catch (e) {
      setFehler(e.message);
    }
  };

  const bestaetigungIgnorieren = async (id) => {
    try {
      await finanzenApi.wiederkehrendIgnorieren(benutzerId, id);
      laden();
    } catch (e) {
      setFehler(e.message);
    }
  };

  const sichtbareKandidaten = kandidaten.filter(k => !ignoriert.has(k.gruppen_schluessel));

  return (
    <div>
      <div style={fs.karte}>
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0 }}>
          Erkennt Buchungen mit gleichem Zahlungspartner/IBAN, ähnlichem Betrag und ähnlichem Buchungstag über mindestens drei
          verschiedene Monate. Reine Anzeige — es wird nichts automatisch mit Mitgliedern, Rechnungen, SEPA oder Rücklastschriften verknüpft.
        </p>
      </div>

      {fehler && <p style={{ color: THEME.colors.danger }}>{fehler}</p>}

      <div style={fs.karte}>
        <h3 style={{ color: THEME.colors.text, marginTop: 0 }}>Möglicherweise monatlich wiederkehrend ({sichtbareKandidaten.length})</h3>
        {!laedt && sichtbareKandidaten.length === 0 && <p style={{ color: THEME.colors.textMuted }}>Keine Kandidaten gefunden.</p>}
        <div style={fs.tabelle}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={fs.th}>Zahlungspartner</th><th style={fs.th}>Anzahl</th>
                <th style={{ ...fs.th, textAlign: "right" }}>Ø Betrag</th><th style={fs.th}>Richtung</th>
                <th style={fs.th}>Letzte Buchung</th><th style={fs.th}>Kategorie</th><th style={fs.th}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {sichtbareKandidaten.map(k => (
                <tr key={k.gruppen_schluessel}>
                  <td style={fs.td}>{k.zahlungspartner_name || "–"}</td>
                  <td style={fs.td}>{k.anzahl}</td>
                  <td style={{ ...fs.td, textAlign: "right" }}>{k.durchschnitts_betrag.toFixed(2)} €</td>
                  <td style={{ ...fs.td, color: k.richtung === "einnahme" ? THEME.colors.success : THEME.colors.danger }}>{k.richtung}</td>
                  <td style={fs.td}>{k.letzte_buchung}</td>
                  <td style={fs.td}>{kategorieName(k.kategorie_id)}</td>
                  <td style={fs.td}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button style={{ ...fs.btnGray, padding: "4px 10px", fontSize: THEME.fontSize.sm, background: THEME.colors.success }} onClick={() => bestaetigen(k)}>Bestätigen</button>
                      <button style={{ ...fs.btnGray, padding: "4px 10px", fontSize: THEME.fontSize.sm }} onClick={() => kategorieZuweisen(k)}>Kategorie</button>
                      <button style={{ ...fs.btnGray, padding: "4px 10px", fontSize: THEME.fontSize.sm }} onClick={() => kandidatIgnorieren(k)}>Ignorieren</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={fs.karte}>
        <h3 style={{ color: THEME.colors.text, marginTop: 0 }}>Bestätigte wiederkehrende Zahlungen ({bestaetigte.length})</h3>
        <div style={fs.tabelle}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={fs.th}>Name</th><th style={fs.th}>Zahlungspartner</th>
                <th style={{ ...fs.th, textAlign: "right" }}>Erwarteter Betrag</th><th style={fs.th}>Rhythmus</th>
                <th style={fs.th}>Kategorie</th><th style={fs.th}></th>
              </tr>
            </thead>
            <tbody>
              {bestaetigte.map(w => (
                <tr key={w.id}>
                  <td style={fs.td}>{w.name}</td>
                  <td style={fs.td}>{w.zahlungspartner_name || "–"}</td>
                  <td style={{ ...fs.td, textAlign: "right" }}>{w.erwarteter_betrag != null ? `${w.erwarteter_betrag.toFixed(2)} €` : "–"}</td>
                  <td style={fs.td}>{w.rhythmus}</td>
                  <td style={fs.td}>{kategorieName(w.kategorie_id)}</td>
                  <td style={fs.td}><button style={fs.btnDanger} onClick={() => bestaetigungIgnorieren(w.id)}>Entfernen</button></td>
                </tr>
              ))}
              {bestaetigte.length === 0 && <tr><td style={fs.td} colSpan={6}>Noch keine bestätigten wiederkehrenden Zahlungen.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
